// import { GA } from "shared/utils/googleAnalytics"
import { toast } from "@suraasa/placebo-ui"
import api from "api"
import { APIError } from "api/utils"
import { Status } from "features/Cpat/Assessments/Proctoring/hooks/useCamDetection"
import { useProctoring } from "features/Cpat/Assessments/Proctoring/hooks/useProctoring"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { isDesktop } from "react-device-detect"
import { useNavigate } from "react-router"
import Webcam from "react-webcam"
import routes from "routes"
import { browserIsSupportedForProctoring } from "utils/constants"
import { mapErrorsV2 } from "utils/helpers"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

import { triggerFullscreen } from "../helpers"
import ProctoringAlerts from "./ProctoringAlerts"

type Options = {
  totalWarnings: number
  testIsAccessible: boolean
  videoRef: React.RefObject<Webcam>
  proctoringAlerts: JSX.Element
  webCamStatus: Status | null
  warnings: WarningType
}
type Props = {
  setWebCamStatus: (status: Status | null) => void
  onTotalWarningsChange: (warningsIncurred: number) => void
  attemptId: string
  disabled?: boolean
  assessmentId: number
  maxWarningCount: number | null
  render: ({
    proctoringAlerts,
    totalWarnings,
    testIsAccessible,
    videoRef,
    warnings,
  }: Options) => JSX.Element
}

export type WarningType = {
  cameraCloseCount: number
  tabSwitchCount: number
  noFaceDetectedCount: number
  unfairMeansDetectedCount: number
}

const Proctoring = ({
  attemptId,
  assessmentId,
  setWebCamStatus,
  onTotalWarningsChange,
  render,
  maxWarningCount,
  disabled = false,
}: Props) => {
  if (import.meta.env.VITE_CPAT_TEST_MODE === "true") {
    disabled = true
  }

  const [warningsFetched, setWarningsFetched] = useState(false)
  const [latestWarning, setLatestWarning] = useState<keyof WarningType>()
  const [warnings, setWarnings] = useState<WarningType>({
    cameraCloseCount: 0,
    tabSwitchCount: 0,
    noFaceDetectedCount: 0,
    unfairMeansDetectedCount: 0,
  })

  const {
    webcam: { webCamStatus, videoRef },
    devTools: { devToolsOpen },
    fullScreen: { fullScreenStatus },
    tabUnfocus: { tabUnfocusStatus },
  } = useProctoring({ disabled })

  useEffect(() => {
    setWebCamStatus(webCamStatus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webCamStatus])

  useEffect(() => {
    if (fullScreenStatus) {
      document.body.style.overflowY = "auto"
    }
  }, [fullScreenStatus])

  const navigate = useNavigate()

  const totalWarnings =
    Object.values(warnings).reduce((number, total) => total + number, 0) || 0

  useEffect(() => {
    onTotalWarningsChange(totalWarnings)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalWarnings])

  /**
   * Retrieve Warning Counts
   */
  useEffect(() => {
    const getWarnings = async () => {
      try {
        const res = await api.cpat.getAttemptWarning({
          urlParams: {
            attemptId,
          },
        })
        if ("autoSubmitted" in res) {
          toast.error("Assessment already submitted.")
          navigate("/dashboard/ito/")
          return
        }
        if ("warningData" in res)
          setWarnings({
            unfairMeansDetectedCount:
              res.warningData?.unfairMeansDetectedCount || 0,
            noFaceDetectedCount: res.warningData?.noFaceDetectedCount || 0,
            tabSwitchCount: res.warningData?.tabSwitchCount || 0,
            cameraCloseCount: res.warningData?.cameraCloseCount || 0,
          })
      } catch (err) {
        console.error(err)
      }
      setWarningsFetched(true)
    }
    getWarnings()
  }, [attemptId, navigate])

  /**
   * Send warning count to backend whenever warning increases
   */
  useEffect(() => {
    const updateWarning = async () => {
      try {
        const res = await api.cpat.updateWarningCount({
          urlParams: { attemptId },
          data: {
            trigger: latestWarning,
            totalWarningCount: totalWarnings,
            warningData: warnings,
          },
        })
        if (res && "attempt" in res) {
          toast.error("Assessment already submitted.")
          navigate(routes.cpat.dashboard)
          return
        }
      } catch (err) {
        if (err instanceof APIError) {
          console.log(err)
          return
        }
        mapErrorsV2(err)
      }
    }

    /**
     * We don't call this API until the GET API resolves. Else we'd be sending 0,0,0 to backend which isn't ideal.
     */
    if (warningsFetched && !disabled) {
      updateWarning()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warnings, disabled])

  const [showSettingsOverlay, toggleSettingsOverlay] = useState(false)
  const [winSwitchDialog, toggleWinSwitchDialog] = useState(false)
  const [showDevToolsDialog, toggleDevToolsDialog] = useState(false)
  const [showFullscreenOverlay, setShowFullscreenOverlay] = useState(false)

  const testIsAccessible =
    disabled ||
    (browserIsSupportedForProctoring === "supported" &&
      !tabUnfocusStatus &&
      webCamStatus === "on" &&
      !showSettingsOverlay &&
      !winSwitchDialog &&
      !showFullscreenOverlay)

  /**
   * Camera Access:
   * When user denies camera permissions or turns off camera,
   * increase camera warning by 1 and make settings overlay visible
   */
  useEffect(() => {
    if (webCamStatus === "blocked" || webCamStatus === "off") {
      toggleSettingsOverlay(true)
      setWarnings(prev => ({
        ...prev,
        cameraCloseCount: prev.cameraCloseCount + 1,
      }))
      setLatestWarning("cameraCloseCount")

      trackingService.trackEvent(
        TrackingEvents.cpatProctoringViolation,
        {
          violation_type: "camera_blocked",
          webcam_status: webCamStatus,
        },
        { services: ["PostHog"] }
      )
    } else toggleSettingsOverlay(false)
  }, [webCamStatus])

  useEffect(() => {
    if (webCamStatus !== null && tabUnfocusStatus) {
      //   trackOnGA("unfocus")

      console.log(`> Warning Count Triggered: Tab Unfocus`)
      setWarnings(prev => ({
        ...prev,
        tabSwitchCount: prev.tabSwitchCount + 1,
      }))
      setLatestWarning("tabSwitchCount")
      toggleWinSwitchDialog(true)

      trackingService.trackEvent(
        TrackingEvents.cpatProctoringViolation,
        {
          violation_type: "tab_switch",
        },
        { services: ["PostHog"] }
      )
    }
  }, [webCamStatus, tabUnfocusStatus])

  useEffect(() => {
    setShowFullscreenOverlay(!fullScreenStatus)

    // Track when user exits fullscreen
    if (fullScreenStatus === false) {
      trackingService.trackEvent(
        TrackingEvents.cpatProctoringViolation,
        {
          violation_type: "exit_fullscreen",
        },
        { services: ["PostHog"] }
      )
    }
  }, [fullScreenStatus])

  const consecutiveFailures = useRef(0)

  const captureAndUploadImage = useCallback(
    async (violationTypeParam?: string) => {
      try {
        if (!videoRef.current || !videoRef.current.video) {
          return
        }

        const video = videoRef.current.video

        if (
          video.readyState < 2 ||
          video.videoWidth === 0 ||
          video.videoHeight === 0
        ) {
          consecutiveFailures.current += 1
          if (consecutiveFailures.current === 5) {
            toast.error(
              "Camera feed seems unstable. Please check your camera connection."
            )
          }
          return
        }

        consecutiveFailures.current = 0

        const base64Image = videoRef.current.getScreenshot({
          width: 640, // Reduced resolution
          height: 480,
        })

        if (base64Image) {
          // Compress the image
          const compressedBlob = await new Promise<Blob>(resolve => {
            const img = new Image()
            img.onload = () => {
              const canvas = document.createElement("canvas")
              const ctx = canvas.getContext("2d")

              // Set smaller dimensions
              const maxWidth = 640
              const maxHeight = 480
              let width = img.width
              let height = img.height

              if (width > height) {
                if (width > maxWidth) {
                  height *= maxWidth / width
                  width = maxWidth
                }
              } else {
                if (height > maxHeight) {
                  width *= maxHeight / height
                  height = maxHeight
                }
              }

              canvas.width = width
              canvas.height = height
              ctx?.drawImage(img, 0, 0, width, height)

              // Compress to JPEG with quality 0.7
              canvas.toBlob(
                compressedBlob => {
                  if (compressedBlob) resolve(compressedBlob)
                },
                "image/jpeg",
                0.7
              )
            }
            img.src = base64Image
          })

          const data = new FormData()
          data.append("assessment_id", assessmentId.toString())
          data.append("attempt_id", attemptId)
          if (violationTypeParam) {
            console.log("> Violation Type:", violationTypeParam)
            data.append("violation_type", violationTypeParam)
          }
          data.append(
            "violation_image",
            new File([compressedBlob], "image.jpg", { type: "image/jpeg" })
          )

          console.log("> Uploading image to backend (compressed)")
          await api.cpat.uploadViolation({ data })
        }
      } catch (e) {
        console.error("Error uploading image:", e)
      }
    },
    [videoRef, assessmentId, attemptId]
  )

  /**
   * Periodic image upload every 2 seconds
   */
  useEffect(() => {
    if (disabled || webCamStatus !== "on") {
      return
    }

    const intervalId = setInterval(() => {
      captureAndUploadImage()
    }, 2000)

    return () => clearInterval(intervalId)
  }, [disabled, webCamStatus, captureAndUploadImage])

  // useEffect(() => {
  //   if (violationType === "noFace") {
  //     console.log(`> Warning Count Triggered: ${violationType}`)
  //     setWarnings(prev => ({
  //       ...prev,
  //       noFaceDetectedCount: prev.noFaceDetectedCount + 1,
  //     }))
  //     captureAndUploadImage("noFace")
  //     setLatestWarning("noFaceDetectedCount")
  //   }
  //   if (violationType === "unfairMeans") {
  //     console.log(`> Warning Count Triggered: ${violationType}`)
  //     setWarnings(prev => ({
  //       ...prev,
  //       unfairMeansDetectedCount: prev.unfairMeansDetectedCount + 1,
  //     }))
  //     captureAndUploadImage("unfairMeans")
  //     setLatestWarning("unfairMeansDetectedCount")
  //     clearViolations()
  //   }

  //   if (violationType === null) {
  //     setLatestWarning(undefined)
  //     clearViolations()
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [violationType, videoRef])

  const proctoringAlerts = (
    <ProctoringAlerts
      maxWarningCount={maxWarningCount}
      disabled={disabled}
      showDevToolsOverlay={devToolsOpen}
      showFullscreenOverlay={showFullscreenOverlay}
      showDevToolsDialog={showDevToolsDialog}
      showSettingsOverlay={showSettingsOverlay}
      showTabUnfocusDialog={winSwitchDialog}
      onClose={(type: "fullscreen" | "tabUnfocus" | "devtools") => {
        if (type === "fullscreen") {
          if (isDesktop) triggerFullscreen()
          setShowFullscreenOverlay(false)
        }
        if (type === "tabUnfocus") {
          if (isDesktop) triggerFullscreen()
          toggleWinSwitchDialog(false)
        }
        if (type === "devtools") {
          toggleDevToolsDialog(false)
        }
      }}
      totalWarnings={totalWarnings}
    />
  )

  return (
    <>
      {/* <pre className="mt-5">
        {JSON.stringify(
          {
            maxWarningCount,
            devToolsOpen,
            fullScreenStatus,
            showFullscreenOverlay,
            showDevToolsDialog,
            showSettingsOverlay,
            winSwitchDialog,
          },
          null,
          2
        )}
      </pre> */}
      {render({
        proctoringAlerts,
        totalWarnings,
        testIsAccessible,
        videoRef,
        webCamStatus,
        warnings,
      })}
    </>
  )
}

export default Proctoring
